/**
 * Generic repeatable instance list + edit orchestrator (#1154).
 * Plain View wrapper — no SubsectionForm around the list (matches RackConfigurations).
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { View, StyleSheet, Platform } from 'react-native';

import { RepeatableInstanceEditFormContent } from './RepeatableInstanceEditFormContent';
import { RepeatableInstanceEditModal } from './RepeatableInstanceEditModal';
import { RepeatableInstanceEditScreen } from './RepeatableInstanceEditScreen';
import { repeatableInstanceSessionHasChanges } from './repeatableInstanceEditSessionUtils';
import { RepeatableInstanceList } from './RepeatableInstanceList';
import { instanceIdsEqual } from './repeatableSectionUtils';
import { useRepeatableInstanceOrchestrator } from './useRepeatableInstanceOrchestrator';
import {
  RACK_DISCARD_UNSAVED_DISCARD_LABEL,
  RACK_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
  RACK_DISCARD_UNSAVED_MESSAGE,
  RACK_DISCARD_UNSAVED_TITLE,
} from '../../constants/rackConfigurationsSubsectionCopy';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import { useDevicesEditorPendingOpen } from '../../hooks/useDevicesEditorPendingOpen';
import { useLocationTagNameLookup } from '../../hooks/useLocationTagNameLookup';
import { offlineCache } from '../../services/offlineCache';
import { devicesEditorNavigation } from '../../utils/devicesEditorNavigation';

import type { RepeatableInstanceEditFormRenderProps } from './repeatableInstanceEditFormTypes';
import type { RepeatableInstanceSaveHandle } from './repeatableInstanceSaveHandle';
import type { RepeatableInstance } from './types';
import type { RepeatableTableLayout } from './types';
import type { RepeatableInstanceEditSessionConfig } from './types';
import type { RepeatableInstanceOrchestratorConfig } from './useRepeatableInstanceOrchestrator';
import type { SavedAttachment } from '../../services/attachmentService';
import type { DeviceStepId } from '../../utils/deviceRowMapper';
import type { SubsectionFieldDescriptor } from '../../utils/subsectionHeaderMetrics';
import type { SelectedFile } from '../forms/FileUpload';

export type { RepeatableInstanceEditSessionConfig } from './types';

export interface RepeatableInstancesSectionLabels {
  entityPrefix: string;
  entityLabelPlural: string;
  addButtonLabel: string;
  emptyStateTitle: string;
  editSaveLabel: string;
  editSaveDraftLabel: string;
  editDeleteLabel: string;
  deleteConfirmTitle: string;
  deleteConfirmMessage: string;
  editAllListLabel: string;
  editFormTitle: string;
  editFormSubtitle?: string;
  editFormMetricFields: readonly SubsectionFieldDescriptor[];
  /**
   * Editor-screen discard dialog copy overrides (#997 Story 6.3 R5). Omitted →
   * the edit screen keeps its generic 'Discard unsaved changes?' copy.
   */
  editDiscardTitle?: string;
  editDiscardMessage?: string;
  editDiscardConfirmLabel?: string;
  editDiscardKeepEditingLabel?: string;
}

export interface RepeatableInstancesSectionTableConfig<
  T extends RepeatableInstance,
> {
  locationTagCellColumn?: string;
  locationTagSublineColumn?: string;
  columnsForLayout: (layout: RepeatableTableLayout) => readonly string[];
  columnAlign?: Record<string, 'left' | 'center'>;
  headerLabel: (column: string, layout: RepeatableTableLayout) => string;
  formatCell: (
    instance: T,
    column: string,
    layout: RepeatableTableLayout,
  ) => string;
  getColumnSubline?: (
    instance: T,
    column: string,
    layout: RepeatableTableLayout,
  ) => string | undefined;
  columnUsesDoubleLine?: (
    column: string,
    layout: RepeatableTableLayout,
  ) => boolean;
  getEditChromeTitle: (
    instanceName: string | undefined,
    options: { isNew?: boolean; displayOrder?: number },
  ) => string;
}

export interface RepeatableInstancesSectionProps<
  T extends RepeatableInstance,
  TPatch,
> extends RepeatableInstanceOrchestratorConfig<T, TPatch> {
  labels: RepeatableInstancesSectionLabels;
  table: RepeatableInstancesSectionTableConfig<T>;
  sectionId: string;
  /** Devices-view identity used to open a pending persisted unit. */
  devicesStepId?: DeviceStepId;
  requestStage?: string;
  showFullDetails?: boolean;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  instanceToFieldMap: (
    instance: T,
  ) => Record<string, string | boolean | number | null | undefined>;
  fieldMapToPatch: (
    fields: Record<string, string | boolean | number | null | undefined>,
  ) => TPatch;
  renderEditFields: (props: {
    fields: Record<string, string | boolean | number | null | undefined>;
    setField: (field: string, value: string | boolean | number | null) => void;
    disabled: boolean;
    instanceMutationMode: boolean;
    requestStage: string;
    requestId?: string;
    mode: 'create' | 'detail';
    /** Restrict to these field-group (`subsectionId`) keys — #997 Story 6.3 per-card. */
    fieldGroupKeys?: readonly string[];
  }) => React.ReactNode;
  /** Full step-parity edit body (main card + shared subsections). Replaces single-card shell. */
  renderEditFormContent?: (
    props: RepeatableInstanceEditFormRenderProps,
  ) => React.ReactNode;
  /** Updates step-level fields (additional info, FP Large UPS, etc.) while instance editor is open. */
  onSharedFieldChange?: (field: string, value: string | boolean) => void;
  registerInstanceSaveHandle?: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  onInstanceEditorActiveChange?: (
    active: boolean,
    footerSaveLabel?: string,
  ) => void;
  editSession?: RepeatableInstanceEditSessionConfig<T>;
  testID?: string;
}

export function RepeatableInstancesSection<
  T extends RepeatableInstance,
  TPatch,
>({
  labels,
  table,
  sectionId,
  devicesStepId,
  requestStage = 'DRAFT',
  showFullDetails = true,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  onCaptionChange,
  pendingCaptionEdits,
  instanceToFieldMap,
  fieldMapToPatch,
  renderEditFields,
  renderEditFormContent,
  onSharedFieldChange,
  registerInstanceSaveHandle,
  onInstanceEditorActiveChange,
  editSession,
  testID = 'repeatable-instances-section',
  requestId,
  ...orchestratorConfig
}: RepeatableInstancesSectionProps<T, TPatch>): React.ReactElement {
  const { persistedInstancesKey } = orchestratorConfig;
  const { confirm, confirmationDialog } = useConfirmationDialog();
  const { resolveName: resolveLocationTagName } =
    useLocationTagNameLookup(requestId);
  const editorSaveTriggerRef = useRef<(() => Promise<void>) | null>(null);
  const editorDismissTriggerRef = useRef<(() => Promise<boolean>) | null>(null);

  const orchestrator = useRepeatableInstanceOrchestrator({
    ...orchestratorConfig,
    requestId,
  });

  const {
    isDraftMode,
    useSessionEdit,
    listReadOnly,
    isInstanceEditMode,
    isEditorVisible,
    isNewInstance,
    selectedInstance,
    sortedInstances,
    baselineSessionInstances,
    editorInitial,
    editorDirtyRef,
    handleReorderSection,
    handleCloseEditor,
    handleInstancePress,
    handleAddInstance,
    handleNavigateToInstance,
    handleDuplicateInstance,
    performDeleteInstance,
    handleSaveInstance,
    resetSessionInstances,
    canDeleteInstanceFromList,
  } = orchestrator;

  useDevicesEditorPendingOpen(
    devicesStepId,
    sortedInstances,
    handleInstancePress,
    {
      isEditorOpen: isEditorVisible,
      currentEditorId: selectedInstance?.id,
    },
  );

  const closeInstanceEditor = useCallback(() => {
    handleCloseEditor();
    if (devicesStepId) {
      devicesEditorNavigation.completeReturnToDevices();
    }
  }, [devicesStepId, handleCloseEditor]);

  const [editorFields, setEditorFields] = useState<
    Record<string, string | boolean | number | null | undefined>
  >(() => instanceToFieldMap(editorInitial));
  const [editorHasChanges, setEditorHasChanges] = useState(false);

  useEffect(() => {
    setEditorFields(instanceToFieldMap(editorInitial));
    editorDirtyRef.current = false;
    setEditorHasChanges(false);
  }, [editorInitial, isEditorVisible, instanceToFieldMap, editorDirtyRef]);

  const setField = useCallback(
    (field: string, value: string | boolean | number | null) => {
      setEditorFields(prev => ({ ...prev, [field]: value }));
      editorDirtyRef.current = true;
      setEditorHasChanges(true);
    },
    [editorDirtyRef],
  );

  const markAttachmentDirty = useCallback(() => {
    editorDirtyRef.current = true;
    setEditorHasChanges(true);
  }, [editorDirtyRef]);

  const markEditorDirty = useCallback(() => {
    editorDirtyRef.current = true;
    setEditorHasChanges(true);
  }, [editorDirtyRef]);

  const setSharedField = useCallback(
    (field: string, value: string | boolean) => {
      markEditorDirty();
      onSharedFieldChange?.(field, value);
    },
    [markEditorDirty, onSharedFieldChange],
  );

  const confirmDeleteInstance = useCallback(
    (): Promise<boolean> =>
      confirm({
        title: labels.deleteConfirmTitle,
        message: labels.deleteConfirmMessage,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmButtonStyle: 'danger',
      }),
    [confirm, labels.deleteConfirmMessage, labels.deleteConfirmTitle],
  );

  const handleDeleteFromList = useCallback(
    async (instance: T) => {
      if (!canDeleteInstanceFromList(instance)) {
        return;
      }
      const confirmed = await confirmDeleteInstance();
      if (!confirmed) {
        return;
      }
      await performDeleteInstance(instance);
      if (
        selectedInstance?.id != null &&
        instance.id != null &&
        instanceIdsEqual(selectedInstance.id, instance.id)
      ) {
        closeInstanceEditor();
      }
    },
    [
      canDeleteInstanceFromList,
      confirmDeleteInstance,
      performDeleteInstance,
      selectedInstance,
      closeInstanceEditor,
    ],
  );

  const isWebInlineEditor = Platform.OS === 'web' && isEditorVisible;
  const isFormDisabled = !isInstanceEditMode;
  const footerSaveLabel = isDraftMode
    ? labels.editSaveDraftLabel
    : labels.editSaveLabel;
  const onInstanceEditorActiveChangeRef = useRef(onInstanceEditorActiveChange);
  onInstanceEditorActiveChangeRef.current = onInstanceEditorActiveChange;

  useEffect(() => {
    onInstanceEditorActiveChange?.(
      isEditorVisible,
      isEditorVisible ? footerSaveLabel : undefined,
    );
  }, [isEditorVisible, footerSaveLabel, onInstanceEditorActiveChange]);

  useEffect(() => () => onInstanceEditorActiveChangeRef.current?.(false), []);

  const formatListCell = useCallback(
    (instance: T, column: string, layout: RepeatableTableLayout) => {
      if (column === table.locationTagCellColumn) {
        return resolveLocationTagName(instance.locationTagId) || '—';
      }
      return table.formatCell(instance, column, layout);
    },
    [resolveLocationTagName, table],
  );

  const getListColumnSubline = useCallback(
    (instance: T, column: string, layout: RepeatableTableLayout) => {
      if (column === table.locationTagSublineColumn) {
        return resolveLocationTagName(instance.locationTagId) || undefined;
      }
      return table.getColumnSubline?.(instance, column, layout);
    },
    [resolveLocationTagName, table],
  );

  const tryDismissInstanceEditor = useCallback(async (): Promise<boolean> => {
    if (!isEditorVisible) {
      return true;
    }
    const dismiss = editorDismissTriggerRef.current;
    if (dismiss) {
      return dismiss();
    }
    if (!editorDirtyRef.current) {
      closeInstanceEditor();
      return true;
    }
    return confirm({
      title: RACK_DISCARD_UNSAVED_TITLE,
      message: RACK_DISCARD_UNSAVED_MESSAGE,
      confirmText: RACK_DISCARD_UNSAVED_DISCARD_LABEL,
      cancelText: RACK_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
      confirmButtonStyle: 'danger',
    }).then(confirmed => {
      if (confirmed) {
        closeInstanceEditor();
      }
      return confirmed;
    });
  }, [confirm, closeInstanceEditor, isEditorVisible, editorDirtyRef]);

  useEffect(() => {
    if (!registerInstanceSaveHandle) {
      return;
    }
    const hasChanges =
      editSession?.hasChanges ?? repeatableInstanceSessionHasChanges;
    const handle: RepeatableInstanceSaveHandle = {
      isInstanceEditorOpen: () => isEditorVisible,
      hasUnsavedInstanceEditor: () => isEditorVisible && editorDirtyRef.current,
      commitOpenInstanceEditor: async () => {
        if (!isEditorVisible) {
          return false;
        }
        const trigger = editorSaveTriggerRef.current;
        if (!trigger) {
          return false;
        }
        try {
          await trigger();
        } catch {
          return false;
        }
        return !editorDirtyRef.current;
      },
      tryDismissInstanceEditor,
      discardOpenInstanceEditor: closeInstanceEditor,
      footerSaveLabel,
      hasPendingListChanges: () => {
        if (!useSessionEdit || !baselineSessionInstances) {
          return false;
        }
        return hasChanges(baselineSessionInstances, sortedInstances);
      },
      flushPendingListChanges: async () => {
        if (!useSessionEdit || !requestId || !baselineSessionInstances) {
          return;
        }
        if (!hasChanges(baselineSessionInstances, sortedInstances)) {
          return;
        }
        if (editSession?.flushSession) {
          const flushed = await editSession.flushSession({
            requestId,
            baseline: baselineSessionInstances,
            session: sortedInstances,
            offlineMode: offlineMode ?? false,
          });
          resetSessionInstances(flushed);
          return;
        }
        await offlineCache.updateCachedRequestRepeatableConfigurations(
          requestId,
          { [persistedInstancesKey]: sortedInstances },
        );
        resetSessionInstances(sortedInstances);
      },
    };
    registerInstanceSaveHandle(handle);
    return () => registerInstanceSaveHandle(null);
  }, [
    registerInstanceSaveHandle,
    isEditorVisible,
    footerSaveLabel,
    tryDismissInstanceEditor,
    closeInstanceEditor,
    editorDirtyRef,
    useSessionEdit,
    baselineSessionInstances,
    sortedInstances,
    editSession,
    requestId,
    offlineMode,
    resetSessionInstances,
    persistedInstancesKey,
  ]);

  const editorInstanceName =
    typeof editorFields.instanceName === 'string'
      ? editorFields.instanceName
      : editorInitial.instanceName;

  const editorInstanceStatus =
    typeof editorFields.instanceStatus === 'string'
      ? editorFields.instanceStatus
      : editorInitial.instanceStatus;

  const setInstanceName = useCallback(
    (value: string) => setField('instanceName', value),
    [setField],
  );
  const setInstanceStatus = useCallback(
    (value: string) => setField('instanceStatus', value),
    [setField],
  );

  const editorTitle = table.getEditChromeTitle(editorInstanceName, {
    isNew: isNewInstance,
    displayOrder: editorInitial.displayOrder,
  });

  const editorProps = {
    instanceTitle: editorTitle,
    backToListLabel: labels.editAllListLabel,
    newInstanceFallbackTitle: editorTitle,
    entityPrefix: labels.entityPrefix,
    onClose: closeInstanceEditor,
    onSave: async () => {
      const mapped = fieldMapToPatch(editorFields);
      const patch = {
        ...mapped,
        displayOrder: editorInitial.displayOrder,
      } as TPatch;
      await handleSaveInstance(patch);
      if (devicesStepId) {
        devicesEditorNavigation.completeReturnToDevices();
      }
    },
    onDelete:
      isInstanceEditMode &&
      !isNewInstance &&
      selectedInstance &&
      canDeleteInstanceFromList(selectedInstance)
        ? async () => {
            const confirmed = await confirmDeleteInstance();
            if (!confirmed) {
              return;
            }
            await performDeleteInstance(selectedInstance);
            closeInstanceEditor();
          }
        : undefined,
    disabled: isFormDisabled,
    hideEditorActions: isWebInlineEditor || !isInstanceEditMode,
    saveLabel: footerSaveLabel,
    deleteLabel: labels.editDeleteLabel,
    discardTitle: labels.editDiscardTitle,
    discardMessage: labels.editDiscardMessage,
    discardConfirmLabel: labels.editDiscardConfirmLabel,
    discardKeepEditingLabel: labels.editDiscardKeepEditingLabel,
    isNew: isNewInstance,
    hasChanges: editorHasChanges,
    onDirtyChange: (dirty: boolean) => {
      editorDirtyRef.current = dirty;
    },
    saveTriggerRef: editorSaveTriggerRef,
    dismissTriggerRef: editorDismissTriggerRef,
    instancesForNavigation: sortedInstances,
    currentInstanceId: selectedInstance?.id,
    onNavigateToInstance: handleNavigateToInstance,
    testID: `${testID}-edit-screen`,
  };

  const editFormFields = useMemo(
    () =>
      renderEditFields({
        fields: editorFields,
        setField,
        disabled: isFormDisabled,
        instanceMutationMode: isInstanceEditMode && !isFormDisabled,
        requestStage,
        requestId,
        mode: isDraftMode ? 'create' : 'detail',
      }),
    [
      renderEditFields,
      editorFields,
      setField,
      isFormDisabled,
      isInstanceEditMode,
      requestStage,
      requestId,
      isDraftMode,
    ],
  );

  // Per-card field renderer (#997 Story 6.3): renders only the fields in the
  // given `subsectionId` groups, so a multi-card edit body can place each field
  // in its own card. Same render path/visibility as the full-field renderer.
  const renderInstanceFieldsInGroups = useCallback(
    (fieldGroupKeys: readonly string[]): React.ReactNode =>
      renderEditFields({
        fields: editorFields,
        setField,
        disabled: isFormDisabled,
        instanceMutationMode: isInstanceEditMode && !isFormDisabled,
        requestStage,
        requestId,
        mode: isDraftMode ? 'create' : 'detail',
        fieldGroupKeys,
      }),
    [
      renderEditFields,
      editorFields,
      setField,
      isFormDisabled,
      isInstanceEditMode,
      requestStage,
      requestId,
      isDraftMode,
    ],
  );

  const sectionAttachments = attachmentsBySection[sectionId] ?? [];
  const sectionSelectedFiles = selectedFiles[sectionId] ?? [];

  const onFilesSelectedDirty = useMemo(
    () =>
      onFilesSelected
        ? (files: SelectedFile[]) => {
            markAttachmentDirty();
            onFilesSelected(sectionId, files);
          }
        : undefined,
    [markAttachmentDirty, onFilesSelected, sectionId],
  );

  const onDeleteAttachmentDirty = useMemo(
    () =>
      onDeleteAttachment
        ? (attachmentId: number) => {
            markAttachmentDirty();
            onDeleteAttachment(attachmentId);
          }
        : undefined,
    [markAttachmentDirty, onDeleteAttachment],
  );

  const onCaptionChangeDirty = useMemo(
    () =>
      onCaptionChange
        ? (attachmentId: number, caption: string) => {
            markAttachmentDirty();
            onCaptionChange(attachmentId, caption);
          }
        : undefined,
    [markAttachmentDirty, onCaptionChange],
  );

  const editFormRenderProps = useMemo(
    (): RepeatableInstanceEditFormRenderProps => ({
      instanceFields: editorFields,
      setInstanceField: setField,
      instanceName: editorInstanceName,
      instanceStatus: editorInstanceStatus,
      setInstanceName,
      setInstanceStatus,
      sharedFields: orchestratorConfig.legacyRequestFields ?? {},
      setSharedField,
      disabled: isFormDisabled,
      instanceMutationMode: isInstanceEditMode && !isFormDisabled,
      requestStage,
      showFullDetails,
      requestId,
      mode: isDraftMode ? 'create' : 'detail',
      editFormTitle: labels.editFormTitle,
      editFormSubtitle: labels.editFormSubtitle,
      editFormMetricFields: labels.editFormMetricFields,
      sectionId,
      // The unit under edit: `editorInitial` is `selectedInstance` (real id) for a
      // saved unit, or a draft carrying the orchestrator temp id
      // (`newInstanceTempIdRef.current`) for a new unit — always a finite id. Edit
      // bodies scope per-unit attachment ids by it (#997 Story 6.1); it is the same
      // id 6.2's flush remaps temp→real on save.
      configurationId: editorInitial.id,
      attachmentsBySection,
      onAttachFiles,
      onDeleteAttachment,
      selectedFiles,
      onFilesSelected,
      offlineMode,
      onCaptionChange,
      pendingCaptionEdits,
      markDirty: markEditorDirty,
      renderInstanceFields: editFormFields,
      renderInstanceFieldsInGroups,
      testID: `${testID}-edit-form`,
    }),
    [
      editorFields,
      editorInitial.id,
      setField,
      editorInstanceName,
      editorInstanceStatus,
      setInstanceName,
      setInstanceStatus,
      orchestratorConfig.legacyRequestFields,
      requestId,
      setSharedField,
      isFormDisabled,
      isInstanceEditMode,
      requestStage,
      showFullDetails,
      isDraftMode,
      labels.editFormTitle,
      labels.editFormSubtitle,
      labels.editFormMetricFields,
      sectionId,
      attachmentsBySection,
      onAttachFiles,
      onDeleteAttachment,
      selectedFiles,
      onFilesSelected,
      offlineMode,
      onCaptionChange,
      pendingCaptionEdits,
      markEditorDirty,
      editFormFields,
      renderInstanceFieldsInGroups,
      testID,
    ],
  );

  const editFormShell = renderEditFormContent ? (
    renderEditFormContent(editFormRenderProps)
  ) : (
    <RepeatableInstanceEditFormContent
      title={labels.editFormTitle}
      titleSubtitle={labels.editFormSubtitle}
      sectionId={sectionId}
      requestId={requestId}
      requestStage={requestStage}
      headerValues={editorFields}
      metricFields={labels.editFormMetricFields}
      disabled={isFormDisabled}
      attachments={sectionAttachments}
      onAttachFiles={onAttachFiles ? () => onAttachFiles(sectionId) : undefined}
      onDeleteAttachment={onDeleteAttachmentDirty}
      selectedFiles={sectionSelectedFiles}
      onFilesSelected={onFilesSelectedDirty}
      isOffline={offlineMode}
      onCaptionChange={onCaptionChangeDirty}
      pendingCaptionEdits={pendingCaptionEdits}
      testID={`${testID}-edit-form`}
    >
      {editFormFields}
    </RepeatableInstanceEditFormContent>
  );

  return (
    <View style={styles.container} testID={testID}>
      {isWebInlineEditor ? (
        <RepeatableInstanceEditScreen {...editorProps} layout="inline">
          {editFormShell}
        </RepeatableInstanceEditScreen>
      ) : (
        <RepeatableInstanceList
          instances={sortedInstances}
          entityPrefix={labels.entityPrefix}
          entityLabelPlural={labels.entityLabelPlural}
          addButtonLabel={labels.addButtonLabel}
          emptyStateTitle={labels.emptyStateTitle}
          columnsForLayout={table.columnsForLayout}
          columnAlign={table.columnAlign}
          headerLabel={table.headerLabel}
          formatCell={formatListCell}
          getColumnSubline={getListColumnSubline}
          columnUsesDoubleLine={table.columnUsesDoubleLine}
          onInstancePress={handleInstancePress}
          onDuplicateInstance={
            isInstanceEditMode ? handleDuplicateInstance : undefined
          }
          onDeleteInstance={
            isInstanceEditMode ? handleDeleteFromList : undefined
          }
          canDeleteInstance={instance =>
            isInstanceEditMode && canDeleteInstanceFromList(instance)
          }
          onAddInstance={handleAddInstance}
          onReorderSection={
            isInstanceEditMode ? handleReorderSection : undefined
          }
          disabled={orchestratorConfig.disabled}
          readOnly={listReadOnly}
          testID={`${testID}-list`}
        />
      )}

      <RepeatableInstanceEditModal {...editorProps} visible={isEditorVisible}>
        {editFormShell}
      </RepeatableInstanceEditModal>

      {confirmationDialog}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
